import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import type { FacebookConnectionTestResult } from '@/api/integrations/integrationService';
import FacebookPermissionList from './FacebookPermissionList';
import { parseFacebookExpiry } from './facebookPermissions';

interface FacebookTestResultProps {
    result: FacebookConnectionTestResult;
}

/**
 * Inline result of "Test" for the Facebook integration: validity, token type, expiry,
 * granted scopes, missing required (red) and missing recommended (amber) permissions.
 */
const FacebookTestResult: React.FC<FacebookTestResultProps> = ({ result }) => {
    const { t } = useTranslation('crm');
    const valid = result.valid ?? result.success ?? false;
    const message = result.message ?? result.error;
    const expiry = parseFacebookExpiry(result.expires_at);
    const hasTokenDetails = !!result.token_type || result.granted_scopes !== undefined;
    const missingRequired = result.missing_required ?? [];
    const missingRecommended = result.missing_recommended ?? [];
    const grantedScopes = result.granted_scopes ?? [];

    return (
        <div className="space-y-3 text-sm" data-testid="facebook-test-result">
            <Alert variant={valid ? 'success' : 'destructive'}>
                {valid ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription>
                    <p className="font-medium">
                        {valid
                            ? t('campaigns.facebookSetup.test.valid')
                            : t('campaigns.facebookSetup.test.invalid')}
                    </p>
                    {message && <p>{message}</p>}
                </AlertDescription>
            </Alert>

            {hasTokenDetails && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                    {result.token_type && (
                        <>
                            <dt className="text-muted-foreground">
                                {t('campaigns.facebookSetup.test.tokenType')}
                            </dt>
                            <dd className="font-medium">{result.token_type}</dd>
                        </>
                    )}
                    <dt className="text-muted-foreground">
                        {t('campaigns.facebookSetup.test.expires')}
                    </dt>
                    <dd className="font-medium">
                        {expiry ? (
                            <DateTimeFormatter date={expiry} />
                        ) : (
                            t('campaigns.facebookSetup.test.neverExpires')
                        )}
                    </dd>
                    <dt className="text-muted-foreground">
                        {t('campaigns.facebookSetup.test.grantedScopes')}
                    </dt>
                    <dd className="flex flex-wrap gap-1">
                        {grantedScopes.length === 0
                            ? t('campaigns.facebookSetup.test.none')
                            : grantedScopes.map(scope => (
                                  <code
                                      key={scope}
                                      className="rounded bg-muted px-1 py-0.5 text-xs"
                                  >
                                      {scope}
                                  </code>
                              ))}
                    </dd>
                </dl>
            )}

            {missingRequired.length > 0 && (
                <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription className="space-y-1">
                        <p className="font-medium">
                            {t('campaigns.facebookSetup.test.missingRequired')}
                        </p>
                        <FacebookPermissionList permissions={missingRequired} />
                    </AlertDescription>
                </Alert>
            )}

            {missingRecommended.length > 0 && (
                <Alert variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="space-y-1">
                        <p className="font-medium">
                            {t('campaigns.facebookSetup.test.missingRecommended')}
                        </p>
                        <FacebookPermissionList permissions={missingRecommended} />
                    </AlertDescription>
                </Alert>
            )}
        </div>
    );
};

export default FacebookTestResult;
